import { Typography } from '@mui/joy';
import {
  DotsSix,
  LockSimple,
  LockSimpleOpen,
  NotePencil,
  PencilSimpleLine,
  Swap,
  Trash,
} from '@phosphor-icons/react';
import { capitalize } from 'lodash';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { ICompanySettings } from 'auth/types';
import { BomPartType } from 'bom/enums';
import { ICliJob } from 'cli/types';
import { ExpandableList } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useCliJobHandler } from 'core/hooks';
import { useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { IConfirmation, ISelectionAction } from 'core/types';
import { parseMessageContent } from 'core/utils';
import { ExternalPartStatusType } from 'parts/enums';
import {
  QuoteEntityType,
  QuotePartListSelectionAction,
  QuoteVersionType,
  ValidationType,
} from 'quotes/enums';
import { QuotePartHelper } from 'quotes/helpers';
import { getPricingTypeLabels, useQuotePartActions } from 'quotes/hooks';
import {
  mapLockUnlockQuoteParts,
  mapQuoteNoQuoteQuoteParts,
  mapResetPricingQuoteParts,
  mapValidateQuoteParts,
} from 'quotes/mappers';
import { useCustomerQuotePartsContext } from 'quotes/states';
import { ICustomerQuote, ICustomerQuotePart } from 'quotes/types';
import { getActiveQuoteParts } from 'quotes/utils';
import { Color, Variant } from 'theme/enums';

import { getResetPricingOptions } from './resetPricingOptions';
import { getOrderActionItem } from './utils';

/**
 * Selection Actions for Customer Quote/Order Part List
 *  - Lock/Unlock
 *  - Move to
 *  - Pricing Actions:
 *        - Update Margin
 *        - Reset Cost
 *        - Reset Price
 *        - Reset Cost & Price
 *        - Discount Cost
 *        - Add to Pricing Profile
 *        - Quote/No Quote (only for Quote entity type)
 *  - Add Alternate Part
 *  - Edit Lines
 *  - Add Note
 *  - Add to Quote (customer, vendor)
 *  - Add to Order (customer, vendor)
 *  - Assign Vendor
 *  - Validate (Quantity/UOM, Order Increment, MOQ, Margin Range, Cost)
 *  - Order (only for Quote entity type)
 *  - Remove Notes
 *  - Remove
 *
 * When current quote version is 'Draft':
 *  - Order not available (for every part type)
 *  - If is selected any of:
 *    > 'Pricing Group Discount' parts, only Move To, Add to Quote/Order and Remove actions are
 *      available
 *    > 'No Quote' quote parts, only Quote, Add to Quote/Order and Remove actions are available
 *    > 'Alternate' quote parts, Move To is not available
 *
 * When current quote version is 'Release':
 *  - Add to pricing profile and Order are available
 *  - If is selected any of:
 *    > 'No Quote' quote parts, no action is available
 */
export const useSelectionActions = (
  quote: ICustomerQuote,
  companySettings: ICompanySettings,
  setConfirmationOption: Dispatch<SetStateAction<IConfirmation>>,
  quoteEntityType = QuoteEntityType.QUOTE
) => {
  const [closeSelection, setCloseSelection] = useState(false);
  const [activeSelectionActionDrawer, setActiveSelectionActionDrawer] =
    useState<QuotePartListSelectionAction>(null);

  const queryClient = useQueryClient();

  const { t } = useTranslation();
  const { handleCliJob } = useCliJobHandler();
  const { duplicate, removeQuoteParts, resetPricing, upsertQuoteParts } = useQuotePartActions({
    notificationPosition: NotificationPosition.TOP,
  });

  const { setDraftVersionChanged, currentQuoteVersion } = useCustomerQuotePartsContext();
  const { showSuccessNotification, isLoadingTopNotificationDisplayed } = useNotificationContext();
  const { selectedRows, gridApi, rows, updateRows } = useAgGridClientTableContext();

  const selectionActions = (): ISelectionAction[] => {
    const isSingleSelection = selectedRows.length === 1;

    const allPartsLocked = selectedRows?.every((part: ICustomerQuotePart) => Boolean(part.locked));
    const allPartsNoQuote = selectedRows?.every((part: ICustomerQuotePart) =>
      Boolean(part.noQuote)
    );
    const allPartsAbandoned = selectedRows?.every(
      (part: ICustomerQuotePart) => part.externalPartStatus === ExternalPartStatusType.ABANDONED
    );

    const selectedPricingGroupDiscounts = selectedRows?.filter(
      (part: ICustomerQuotePart) => BomPartType.DISCOUNT === part.type
    );
    const hasPricingGroupDiscountSelected = selectedPricingGroupDiscounts?.length > 0;
    const hasNoQuotePartsSelected = selectedRows?.some(
      (part: ICustomerQuotePart) => 1 === part.noQuote
    );
    const hasAlternatePartsSelected = selectedRows?.some(
      (part: ICustomerQuotePart) => !!part?.secondaryLine
    );
    const hasSubstitutedPartsSelected = selectedRows?.some(
      (part: ICustomerQuotePart) => !!part?.substituted
    );
    const hasLockedPartsSelected = selectedRows?.some((part: ICustomerQuotePart) => !!part?.locked);

    const disablePricingGroupsAndNoQuoteActions =
      hasPricingGroupDiscountSelected || hasNoQuotePartsSelected;

    const disabledAddToQuoteOrderActions = isLoadingTopNotificationDisplayed || allPartsAbandoned;

    const orderActionItem = getOrderActionItem(
      selectedRows,
      currentQuoteVersion,
      Number(companySettings?.minimumOrderThreshold),
      setConfirmationOption,
      (confirmationOption?: IConfirmation) => {
        if (confirmationOption) {
          setConfirmationOption(confirmationOption);

          return;
        }

        setConfirmationOption(null);
        setActiveSelectionActionDrawer(QuotePartListSelectionAction.CREATE_ORDER);
      },
      () => setConfirmationOption(null),
      quoteEntityType,
      true
    );

    const addToPricingProfileActionItem = {
      label: t('buttonLabelAddToPricingProfile'),
      disabled: isLoadingTopNotificationDisplayed || disablePricingGroupsAndNoQuoteActions,
      onClick: () =>
        setActiveSelectionActionDrawer(QuotePartListSelectionAction.ADD_TO_PRICING_PROFILE),
    };

    const addToQuoteActionItem = {
      label: t('buttonLabelAddToQuote', { entityType: QuoteEntityType.QUOTE }),
      options: [
        {
          label: t('buttonLabelAddToCustomerQuote', { entityType: QuoteEntityType.QUOTE }),
          onClick: () =>
            setActiveSelectionActionDrawer(QuotePartListSelectionAction.ADD_TO_CUSTOMER_QUOTE),
        },
        {
          label: t('buttonLabelAddToVendorQuote', { entityType: QuoteEntityType.QUOTE }),
          onClick: () =>
            setActiveSelectionActionDrawer(QuotePartListSelectionAction.ADD_TO_VENDOR_QUOTE),
        },
      ],
      disabled: disabledAddToQuoteOrderActions,
    };

    const addToOrderActionItem = {
      label: t('buttonLabelAddToQuote', { entityType: QuoteEntityType.ORDER }),
      options: [
        {
          label: t('buttonLabelAddToCustomerQuote', { entityType: QuoteEntityType.ORDER }),
          onClick: () =>
            setActiveSelectionActionDrawer(QuotePartListSelectionAction.ADD_TO_CUSTOMER_ORDER),
        },
        {
          label: t('buttonLabelAddToVendorQuote', { entityType: QuoteEntityType.ORDER }),
          onClick: () =>
            setActiveSelectionActionDrawer(QuotePartListSelectionAction.ADD_TO_VENDOR_ORDER),
        },
      ],
      disabled: disabledAddToQuoteOrderActions,
    };

    if (QuoteVersionType.RELEASE === currentQuoteVersion) {
      return [
        addToQuoteActionItem,
        addToOrderActionItem,
        addToPricingProfileActionItem,
        {
          ...orderActionItem,
          disabled: isLoadingTopNotificationDisplayed || orderActionItem?.disabled,
          hide: QuoteEntityType.QUOTE !== quoteEntityType,
        },
      ];
    }

    const removeActionItem = getRemoveActionItem(
      selectedPricingGroupDiscounts,
      hasPricingGroupDiscountSelected,
      isLoadingTopNotificationDisplayed
    );

    const assignVendorActionItem = companySettings?.supplierEnabled
      ? {
          label: t('buttonLabelAssignVendor'),
          color: Color.PRIMARY,
          onClick: () => setActiveSelectionActionDrawer(QuotePartListSelectionAction.ASSIGN_VENDOR),
          disabled:
            isLoadingTopNotificationDisplayed ||
            disablePricingGroupsAndNoQuoteActions ||
            hasLockedPartsSelected ||
            hasSubstitutedPartsSelected,
        }
      : null;

    return [
      {
        label: t(allPartsLocked ? 'buttonLabelUnlock' : 'buttonLabelLock'),
        startDecorator: allPartsLocked ? <LockSimpleOpen /> : <LockSimple />,
        onClick: () => lockUnlockAction(selectedRows, allPartsLocked),
        disabled: isLoadingTopNotificationDisplayed || disablePricingGroupsAndNoQuoteActions,
      },
      {
        label: t('buttonLabelMoveCopy'),
        startDecorator: <DotsSix />,
        options: [
          {
            label: t('buttonLabelMoveTo'),
            onClick: () => setActiveSelectionActionDrawer(QuotePartListSelectionAction.MOVE_TO),
          },
          {
            label: t('buttonLabelDuplicate'),
            onClick: () => {
              setConfirmationOption({
                color: Color.WARNING,
                title: t('titleDuplicateLine', { count: selectedRows?.length }),
                message: getDuplicateConfirmationMessage(),
                confirmBtnLabel: t('buttonLabelDuplicate'),
                confirmBtnColor: Color.WARNING,
                onConfirm: duplicateQuotePartsAction,
                onCancel: () => setConfirmationOption(null),
              });
            },
          },
        ],
        disabled: isLoadingTopNotificationDisplayed || hasAlternatePartsSelected,
      },
      {
        label: t('buttonLabelPricingActions'),
        options: [
          {
            label: t('labelUpdateMargin'),
            onClick: () =>
              setActiveSelectionActionDrawer(QuotePartListSelectionAction.UPDATE_MARGIN),
            disabled: disablePricingGroupsAndNoQuoteActions,
          },
          ...getResetPricingOptions({
            onReset: (...args) => resetPricingAction(selectedRows, ...args),
            disabled: disablePricingGroupsAndNoQuoteActions,
          }),
          {
            label: t('buttonLabelApplyCostDiscount'),
            onClick: () =>
              setActiveSelectionActionDrawer(QuotePartListSelectionAction.APPLY_COST_DISCOUNT),
            disabled: disablePricingGroupsAndNoQuoteActions,
          },
          addToPricingProfileActionItem,
          {
            label: t(allPartsNoQuote ? 'buttonLabelQuote' : 'buttonLabelNoQuote'),
            onClick: () => quoteNoQuoteAction(selectedRows, allPartsNoQuote),
            hide: QuoteEntityType.ORDER === quoteEntityType,
          },
        ],
        disabled: isLoadingTopNotificationDisplayed || hasPricingGroupDiscountSelected,
      },
      {
        label: t('buttonLabelAddAlternateLine'),
        startDecorator: <Swap />,
        onClick: () =>
          setActiveSelectionActionDrawer(QuotePartListSelectionAction.ADD_ALTERNATE_PART),
        disabled:
          isLoadingTopNotificationDisplayed ||
          !isSingleSelection ||
          disablePricingGroupsAndNoQuoteActions ||
          hasAlternatePartsSelected,
      },
      {
        label: t('buttonLabelEditLines'),
        color: Color.PRIMARY,
        startDecorator: <PencilSimpleLine />,
        onClick: () => setActiveSelectionActionDrawer(QuotePartListSelectionAction.UPDATE_PARTS),
        disabled:
          isLoadingTopNotificationDisplayed ||
          disablePricingGroupsAndNoQuoteActions ||
          hasLockedPartsSelected ||
          hasSubstitutedPartsSelected,
      },
      {
        label: t('buttonLabelAddNote'),
        color: Color.PRIMARY,
        startDecorator: <NotePencil />,
        onClick: () => setActiveSelectionActionDrawer(QuotePartListSelectionAction.ADD_NOTE),
        disabled: isLoadingTopNotificationDisplayed || disablePricingGroupsAndNoQuoteActions,
      },
      addToQuoteActionItem,
      addToOrderActionItem,
      assignVendorActionItem,
      {
        label: t('buttonLabelValidate'),
        color: Color.PRIMARY,
        options: [
          {
            label: t('labelQuantityUOM'),
            onClick: () =>
              validateAction(selectedRows, ValidationType.QUANTITY_UOM_VALIDATION_STATUS),
          },
          {
            label: t('labelOrderIncrement'),
            onClick: () =>
              validateAction(selectedRows, ValidationType.ORDER_INCREMENT_VALIDATION_STATUS),
          },
          {
            label: t('labelMOQ'),
            onClick: () => validateAction(selectedRows, ValidationType.MOQ_VALIDATION_STATUS),
          },
          {
            label: t('labelMarginRange'),
            onClick: () => validateAction(selectedRows, ValidationType.GPM_VALIDATION_STATUS),
          },
          {
            label: t('labelCost'),
            onClick: () => validateAction(selectedRows, ValidationType.COST_VALIDATION_STATUS),
          },
        ],
        disabled: isLoadingTopNotificationDisplayed || disablePricingGroupsAndNoQuoteActions,
      },
      {
        label: t('buttonLabelRemoveNotes'),
        color: Color.DANGER,
        variant: Variant.SOFT,
        startDecorator: <Trash />,
        onClick: () => setActiveSelectionActionDrawer(QuotePartListSelectionAction.REMOVE_NOTES),
        disabled: isLoadingTopNotificationDisplayed || disablePricingGroupsAndNoQuoteActions,
      },
      removeActionItem,
    ].filter((action: ISelectionAction) => !!action);
  };

  const lockUnlockAction = async (
    selectedRows: ICustomerQuotePart[],
    allPartsLocked = false
  ): Promise<boolean> => {
    const { success, quoteParts } = await upsertQuoteParts(
      mapLockUnlockQuoteParts(selectedRows, allPartsLocked)
    );

    if (success) {
      updateRowsWithUpdatedQuoteParts(quoteParts, {
        refreshCells: false,
        redrawRows: false,
      });

      return true;
    }

    return false;
  };

  const getRemoveActionItem = (
    selectedPricingGroupDiscounts: ICustomerQuotePart[],
    hasPricingGroupDiscountSelected: boolean,
    disabled: boolean
  ): ISelectionAction => {
    const removeConfirmationMessage = !hasPricingGroupDiscountSelected
      ? t('messageConfirmRemoveLines', { count: selectedRows?.length })
      : selectedPricingGroupDiscounts?.length === selectedRows?.length
      ? t('messageConfirmRemovePricingGroupDiscount', {
          count: selectedPricingGroupDiscounts?.length,
        })
      : t('messageConfirmRemoveLinesWithPricingGroupDiscount', {
          partsCount: selectedRows?.length,
          count: selectedPricingGroupDiscounts?.length,
        });

    return {
      label: t('buttonLabelRemove'),
      color: Color.DANGER,
      variant: Variant.SOFT,
      startDecorator: <Trash />,
      onClick: () =>
        setConfirmationOption({
          title: t('titleRemoveLines', { count: selectedRows?.length }),
          message: removeConfirmationMessage,
          confirmBtnLabel: t('buttonLabelRemove'),
          confirmBtnColor: Color.DANGER,
          onConfirm: removeQuotePartsAction,
          onCancel: () => setConfirmationOption(null),
        }),
      disabled,
    };
  };

  const removeQuotePartsAction = async () => {
    setConfirmationOption(null);

    const removedQuotePartsIds = selectedRows?.map((part: ICustomerQuotePart) => part.id);

    handleCliJob(
      removeQuoteParts,
      {
        ids: removedQuotePartsIds,
        quoteId: quote.id,
      },
      {
        cliJobInProgressMessage: t('messageLinesRemoveInProgress', {
          count: selectedRows.length,
        }),
        onCliJobFinished: () => {
          showSuccessNotification(
            { message: t('messageLinesRemoveSucces', { count: selectedRows.length }) },
            NotificationPosition.TOP
          );

          setDraftVersionChanged();
          setCloseSelection(true);

          gridApi?.setGridOption('loading', true);
          queryClient.invalidateQueries('customerQuoteParts');
        },
      }
    );
  };

  const getDuplicateConfirmationMessage = () => {
    if (selectedRows?.length === 1) {
      return t('messageConfirmDuplicateLine', {
        count: selectedRows?.length,
        partNumber: QuotePartHelper.getDisplayedPartNumber(selectedRows[0]),
      });
    }

    const allPartNumbers = selectedRows.map((row) => QuotePartHelper.getDisplayedPartNumber(row));

    return (
      <>
        <Typography>
          {parseMessageContent(
            t('messageConfirmDuplicateLinePrefix', {
              count: selectedRows?.length,
            })
          )}
        </Typography>
        <ExpandableList<string>
          items={allPartNumbers}
          itemRenderer={(partNumber) => (
            <Typography sx={{ fontWeight: 600 }}>{partNumber}</Typography>
          )}
          getItemKey={(partNumber) => partNumber}
          marker="disc"
          maxItems={5}
        />
        <Typography>{t('messageConfirmDuplicateLineSuffix')}</Typography>
      </>
    );
  };

  const duplicateQuotePartsAction = async () => {
    setConfirmationOption(null);

    const duplicateQuotePartsIds = selectedRows?.map((part: ICustomerQuotePart) => part.id);

    handleCliJob(
      duplicate,
      {
        ids: duplicateQuotePartsIds,
      },
      {
        cliJobInProgressMessage: t('messageLineDuplicateInProgress', {
          count: selectedRows.length,
        }),
        onCliJobFinished: (cliJob: ICliJob) => {
          const { quoteParts } = cliJob.result as { quoteParts: ICustomerQuotePart[] };

          showSuccessNotification(
            { message: t('messageLineDuplicateSuccess', { count: selectedRows.length }) },
            NotificationPosition.TOP
          );

          const addIndex = getActiveQuoteParts(rows as ICustomerQuotePart[]).length;

          updateRows({ add: quoteParts, addIndex });

          setDraftVersionChanged();
          setCloseSelection(true);
        },
      }
    );
  };

  const quoteNoQuoteAction = async (
    selectedRows: ICustomerQuotePart[],
    allPartsNoQuote = false
  ) => {
    const { success, quoteParts } = await upsertQuoteParts(
      mapQuoteNoQuoteQuoteParts(selectedRows, allPartsNoQuote)
    );

    if (success) {
      updateRowsWithUpdatedQuoteParts(quoteParts);

      setCloseSelection(true);
    }
  };

  const validateAction = async (selectedRows: ICustomerQuotePart[], type: ValidationType) => {
    const { success, quoteParts } = await upsertQuoteParts(
      mapValidateQuoteParts(selectedRows, type)
    );

    if (success) {
      updateRowsWithUpdatedQuoteParts(quoteParts);

      if (ValidationType.COST_VALIDATION_STATUS === type) {
        setActiveSelectionActionDrawer(QuotePartListSelectionAction.RESET_PRICING);
      } else {
        setCloseSelection(true);
      }
    }
  };

  const resetPricingAction = async (
    selectedRows: ICustomerQuotePart[],
    cost = false,
    price = false,
    excludeCustom = false
  ) => {
    const data = {
      ...mapResetPricingQuoteParts({ cost, price }, selectedRows),
      omitCustomCost: excludeCustom && cost ? 1 : 0,
      omitCustomPrice: excludeCustom && price ? 1 : 0,
    };

    handleCliJob(resetPricing, data, {
      cliJobInProgressMessage: t('messageResetPricingTypeInProgress', {
        type: getPricingTypeLabels(data?.pricingTypes),
      }),
      onCliJobFinished: (cliJob: ICliJob) => {
        const quoteParts = cliJob.result as ICustomerQuotePart[];

        updateRowsWithUpdatedQuoteParts(quoteParts);
        setCloseSelection(true);

        showSuccessNotification(
          {
            message: t('messageResetPricingTypeSuccess', {
              type: capitalize(getPricingTypeLabels(data?.pricingTypes)),
              count: data?.pricingTypes.length,
            }),
          },
          NotificationPosition.TOP
        );
      },
    });
  };

  const updateRowsWithUpdatedQuoteParts = (
    updatedQuoteParts: ICustomerQuotePart[],
    { refreshCells, redrawRows } = {
      refreshCells: true,
      redrawRows: true,
    }
  ) => {
    setDraftVersionChanged();
    updateRows({ update: updatedQuoteParts }, { refreshCells, redrawRows });
  };

  const onCloseSelectionActionDrawer = (closeSelection = false) => {
    setActiveSelectionActionDrawer(null);
    setCloseSelection(closeSelection);
  };

  return {
    activeSelectionActionDrawer,
    setActiveSelectionActionDrawer,
    closeSelection,
    onCloseSelectionActionDrawer,
    selectionActions,
    setCloseSelection,
  };
};
